/**
 * Landing Page
 * Shows hero section and demo brand switcher
 */

import React from 'react';
import { Link } from 'react-router-dom';
import BuildTag from '../components/BuildTag';

const demoBrands = [
    {
        slug: 'topsail-hardware',
        name: 'Topsail Hardware',
        industry: 'Retail',
        description: 'Local hardware store with tools, lumber, and home improvement supplies',
        icon: '🔨',
        color: '#d97706'
    },
    {
        slug: 'harbor-ridge-municipal',
        name: 'Harbor Ridge Municipal',
        industry: 'Government',
        description: 'City services, permits, trash schedules, and community information',
        icon: '🏛️',
        color: '#059669'
    },
    {
        slug: 'captains-table',
        name: "The Captain's Table",
        industry: 'Restaurant',
        description: 'Waterfront dining with fresh seafood and nautical atmosphere',
        icon: '🍽️',
        color: '#dc2626'
    },
    {
        slug: 'coastal-auto',
        name: 'Coastal Auto Group',
        industry: 'Automotive',
        description: 'New and certified pre-owned vehicles, service, financing, and parts',
        icon: '🚗',
        color: '#2563eb'
    },
    {
        slug: 'atlantic-shore-realty',
        name: 'Atlantic Shore Realty',
        industry: 'Real Estate',
        description: 'Full-service brokerage covering Yarmouth, Digby, Shelburne, and the South Shore',
        icon: '🏠',
        color: '#7c3aed'
    },
    {
        slug: 'south-shore-trades',
        name: 'South Shore Trades',
        industry: 'HVAC / Plumbing',
        description: 'Heat pumps, furnaces, water heaters, and 24/7 emergency service',
        icon: '🔧',
        color: '#0891b2'
    }
];

function LandingPage() {
    return (
        <div className="landing-page">
            {/* Navbar */}
            <nav className="navbar navbar-dark bg-lighthouse-gradient">
                <div className="container">
                    <Link to="/" className="navbar-brand mb-0 h1">
                        ⚓ Lighthouse
                    </Link>
                    <div className="d-flex gap-3 align-items-center">
                        <Link to="/home" className="nav-link text-white">Home</Link>
                        <Link to="/subscription" className="nav-link text-white">Pricing</Link>
                        <span className="text-white-50 small d-none d-md-inline">AI Customer Assistance Platform</span>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero bg-lighthouse-gradient text-white py-5">
                <div className="container text-center py-5">
                    <h1 className="display-3 fw-bold mb-4">
                        ⚓ Never miss a customer again
                    </h1>
                    <p className="lead mb-4">
                        Lighthouse is a 24/7 AI assistant for local business — answering on
                        web chat, text, and phone, day or night.
                    </p>
                    <p className="text-white-50">
                        Every after-hours question answered, every lead captured — for local retail,
                        automotive, real estate, trades, government, and hospitality businesses.
                    </p>
                </div>
            </section>

            {/* What is Lighthouse — product explainer */}
            <section className="what-is py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3">What is Lighthouse?</h2>
                        <p className="text-muted col-lg-8 mx-auto">
                            An always-on assistant that answers your customers the moment they reach out —
                            so a phone that rings after close or a question at midnight never turns into a lost sale.
                        </p>
                    </div>

                    <div className="row g-4">
                        <div className="col-md-6 col-lg-3">
                            <div className="p-3 h-100">
                                <div className="h3 mb-2">🕐</div>
                                <h5 className="fw-bold">Answers 24/7</h5>
                                <p className="text-muted mb-0 small">
                                    Instant replies day or night — after hours, weekends, and holidays included.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="p-3 h-100">
                                <div className="h3 mb-2">💬</div>
                                <h5 className="fw-bold">Web, SMS & voice</h5>
                                <p className="text-muted mb-0 small">
                                    Meet customers where they are — web chat, text message, or a real phone call.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="p-3 h-100">
                                <div className="h3 mb-2">📋</div>
                                <h5 className="fw-bold">Captures & books</h5>
                                <p className="text-muted mb-0 small">
                                    Answers FAQs, captures leads, triages urgent calls, and hands off to your team.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="p-3 h-100">
                                <div className="h3 mb-2">🍁</div>
                                <h5 className="fw-bold">Sovereign & bilingual</h5>
                                <p className="text-muted mb-0 small">
                                    Powered by Augure — sovereign Canadian AI — answering in English & French,
                                    with guardrails that keep it on-brand.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured: real customer, live in the wild */}
            <section className="featured-live bg-lighthouse-light py-5">
                <div className="container">
                    <div className="row align-items-center g-4">
                        <div className="col-lg-7">
                            <span className="badge bg-success mb-3">● Live now · Real customer</span>
                            <h2 className="fw-bold mb-3">
                                🏡 Chestnut Holler Guesthouse
                            </h2>
                            <p className="text-muted mb-3">
                                Not a mock-up — this is Lighthouse running in production. Chestnut Holler's
                                guests get an always-on local concierge for the Barrington &amp; South Shore area:
                                things to do, where to eat, the best beaches and sights — over web chat, text, and
                                voice, in <strong>English and French</strong>.
                            </p>
                            <p className="text-muted mb-4 small">
                                It's the same platform behind the demos below — just pointed at a real business's
                                own knowledge, phone number, and voice.
                            </p>
                            <Link to="/demo/chestnut-holler" className="btn btn-success btn-lg">
                                Talk to the concierge →
                            </Link>
                        </div>
                        <div className="col-lg-5 text-center">
                            <div className="card border-0 shadow-sm p-4">
                                <div className="display-1 mb-3">🏡</div>
                                <p className="fst-italic text-muted mb-1">
                                    "What's there to do around Barrington?"
                                </p>
                                <p className="fst-italic text-muted mb-0">
                                    "Quels sont les restaurants du coin?"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Demo Brands Section */}
            <section className="demo-brands py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3">Try a Live Demo</h2>
                        <p className="text-muted">
                            Explore Lighthouse with one of our sample businesses — each wired to its own
                            hours, products, and services.
                        </p>
                    </div>

                    <div className="row g-4">
                        {demoBrands.map((brand) => (
                            <div key={brand.slug} className="col-md-4 col-lg-4">
                                <Link
                                    to={`/demo/${brand.slug}`}
                                    className="text-decoration-none"
                                >
                                    <div className="card card-hover h-100 border-0 shadow">
                                        <div className="card-body text-center p-4">
                                            <div
                                                className="display-1 mb-3"
                                                style={{ fontSize: '4rem' }}
                                            >
                                                {brand.icon}
                                            </div>
                                            <h3 className="h4 fw-bold mb-2">{brand.name}</h3>
                                            <p className="text-muted small mb-3">
                                                <span
                                                    className="badge"
                                                    style={{ backgroundColor: brand.color, color: 'white' }}
                                                >
                                                    {brand.industry}
                                                </span>
                                            </p>
                                            <p className="text-muted mb-0">
                                                {brand.description}
                                            </p>
                                        </div>
                                        <div className="card-footer bg-transparent border-0 text-center pb-4">
                                            <span className="btn btn-primary">
                                                Try Demo →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why-trust Section — safe to put in front of your customers */}
            <section className="features bg-lighthouse-light py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-0">Safe to put in front of your customers</h2>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-4 text-center">
                            <div className="display-4 text-primary mb-3">🎯</div>
                            <h4>On-brand, on the rails</h4>
                            <p className="text-muted">
                                Answers only from your own knowledge, with guardrails that keep it from guessing or going off-topic
                            </p>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="display-4 text-primary mb-3">🔒</div>
                            <h4>Secure & private</h4>
                            <p className="text-muted">
                                Multi-tenant architecture with complete data isolation between businesses
                            </p>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="display-4 text-primary mb-3">🍁</div>
                            <h4>Sovereign Canadian AI</h4>
                            <p className="text-muted">
                                Powered by Augure — your customers' conversations stay on Canadian infrastructure
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-dark text-white-50 py-4">
                <div className="container text-center">
                    <p className="mb-1">
                        Built with ⚓ by Topsail Software Inc.
                    </p>
                    <BuildTag />
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
